import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import DocumentViewer from 'DesignSystem/DataDisplay/DocumentViewer/DocumentViewer';
import { Box, Grid, styled } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { GET_DOCUMENT } from 'graphql/secure/queries';
import Loader from 'Components/Forms/Loader';
import EditForm from 'FeatureModules/Document/Components/Forms/EditDocumentForm';
import { TCreateDocumentInput } from 'graphql/secure/mutations/types/document-mutation.types';
import { TDocumentCategory, IBaseDocument } from 'FeatureModules/Document/types/document.types';
import useMessages from 'hooks/useMessages';
import useDialog from 'hooks/useDialog';
import { UPDATE_DOCUMENT } from 'graphql/secure/mutations';
import { EDocumentOverviewType } from '../types/document.types';
import { EDocumentCategory } from 'FeatureModules/Document/types/document.types';
import { OTHER_CERTIFICATE_ID } from '../utils/document.utils';
import useUploadDialogActions from 'FeatureModules/Document/hooks/useUploadDialogActions';
import { useNavigate } from 'react-router-dom';
import { ThemeButton } from 'DesignSystem';
import { BoxArrowUpRight as ExternalLink } from '@styled-icons/bootstrap/BoxArrowUpRight';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ErrorState from 'Components/ErrorState';

interface IDocumentOverview {
  documentType: keyof typeof EDocumentOverviewType;
}

const ImageDocumentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 'auto ',
}));

const ImageDocumentControls = styled('a')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 2,
  boxShadow: theme.custom.shadows[1],
}));

const DocumentOverview: FC<IDocumentOverview> = ({ documentType }) => {
  const { documentId, id: companyId } = useParams<{ id?: string; documentId: string }>();
  const { error, loading, data } = useQuery<{ document: IBaseDocument }>(GET_DOCUMENT, {
    variables: { id: documentId },
  });
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { openDialog } = useDialog();
  const { handleDeleteDocuments } = useUploadDialogActions();
  const navigate = useNavigate();

  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    onCompleted: ({ updateDocument: { document } }) => {
      // open the request certification dialog if document is certification but has no id
      if (document.category === 'CERTIFICATE' && !document.certificateCatalogItem) {
        openDialog({ type: 'REQUEST_CERTIFICATION', props: { documentTitle: document.title } });
      }
      setSuccessMessage('Document updated successfully');
    },
    onError: error => {
      if (error.message === 'DOCUMENT_UPDATE_FORBIDDEN_IF_USED') {
        setErrorMessage('You cannot update this document because it is already used in a request.');
      } else {
        setErrorMessage('There was an error updating your Document');
      }
    },
  });

  const handleDeleteDocument = async (id: string) => {
    await handleDeleteDocuments({ variables: { ids: [id] } });
    navigate(`/documents/${EDocumentOverviewType[documentType]}`);
  };

  const handleSave = async (values: TCreateDocumentInput) => {
    const selectedCategory = Object.keys(EDocumentCategory).find(
      key => EDocumentCategory[key as TDocumentCategory] === (values.category as TDocumentCategory)
    );

    await updateDocument({
      variables: {
        id: data?.document.id,
        input: {
          title: values.title,
          expiryDate: values.expiryDate,
          issuedDate: values.issuedDate,
          category: selectedCategory,
          comment: values.comment,
          ownedById: values.ownedBy?.id || null,
          linkedSiteIds: values.linkedSites?.map(site => site.id) || [],
          visibility: values.visibility,
          certificateCatalogItemId:
            !values.certificateCatalogItemId?.length ||
            values.certificateCatalogItemId === OTHER_CERTIFICATE_ID
              ? null
              : values.certificateCatalogItemId,
        },
      },
    });
  };

  const getBackUrl: () => string = () => {
    if (documentType === 'PARTNER') {
      return `/partners/${companyId}/documents`;
    }

    return `/documents/${EDocumentOverviewType[documentType]}`;
  };

  if (!data && loading) return <Loader />;
  if (error || !data?.document) return <ErrorState />;

  const document = data.document;

  return (
    <PageContainer>
      <PageTitle
        title="Document overview"
        enableAutoGoBackHistoryButton
        titleExtension={document.title}
        goBackLabel="All documents"
        goBackUrl={getBackUrl()}
      />
      <Box mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {document.title.endsWith('.pdf') ? (
              <DocumentViewer file={document.url} displayLink />
            ) : (
              <ImageDocumentContainer>
                <ImageDocumentControls>
                  <ThemeButton
                    variant="contained"
                    color="WHITE"
                    size="large"
                    onClick={() => window.open(document.url, '_blank')}
                    startIcon={<ExternalLink size={12} />}
                  >
                    Open in new tab
                  </ThemeButton>
                </ImageDocumentControls>
                <img src={document.url} alt={document.title} width="100%" />
              </ImageDocumentContainer>
            )}
          </Grid>
          <Grid item xs={6}>
            {document && (
              <EditForm
                onSubmit={handleSave}
                onDelete={handleDeleteDocument}
                document={document}
                documentType={documentType}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default DocumentOverview;
